import { useEffect, useState } from 'react';
import { useT } from '../../i18n';
import { type ClubList, failedListReads, subscribeListReads } from '../../lib/api/listReadStatus';

// ─────────────────────────────────────────────────────────────────────
// «NOE AV KLUBBENS DATA KUNNE IKKE HENTES» (#1247, RETRO-011).
//
// A failed list read answers `[]`, and `[]` looks exactly like a club with no
// bookings, no activities and no matches. Staff were shown that with nothing
// to say it was not true. This names the lists that are missing, so an empty
// calendar is not read as free courts.
//
// The same shape as «Serveren svarer ikke» (`ServerUnreachableBanner`): not
// a modal, nothing blocked, and it clears itself — the next successful read
// of a list takes it off. Not sticky, so it never covers that banner, which
// usually explains the same outage and is read first.
// ─────────────────────────────────────────────────────────────────────

/** Display order, and the catalog key each list is named by. */
const LISTS: ReadonlyArray<readonly [ClubList, `listLoad.${ClubList}`]> = [
  ['bookings', 'listLoad.bookings'],
  ['activities', 'listLoad.activities'],
  ['singleMatches', 'listLoad.singleMatches'],
  ['plannedMatches', 'listLoad.plannedMatches'],
];

export function ListLoadFailedBanner() {
  const t = useT();
  const [failed, setFailed] = useState(failedListReads);
  useEffect(() => subscribeListReads(setFailed), []);

  if (failed.size === 0) return null;
  const lists = LISTS.filter(([list]) => failed.has(list))
    .map(([, key]) => t(key))
    .join(', ');

  return (
    <div
      data-testid="list-load-failed-banner"
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-sm"
      style={{ background: '#7c2d12', color: '#fed7aa' }}
    >
      <span aria-hidden="true">●</span>
      <span className="font-semibold">{t('listLoad.title')}</span>
      <span className="opacity-90">{t('listLoad.body', { lists })}</span>
    </div>
  );
}
